// MACHINERY V2 (Stroje) — catalog presentation atoms shared by the card, the
// detail aside and the add dialog (M2.8).
//
// Spec: data package §11 field table + §13 ("Catalog Category (int PK)").
// Artboards 0900 / 0901 (cards), 0902 (detail), 0903 (dialog).
//
// >>> THE CATEGORY BADGE IS AN i18n GAP BEING FIXED, NOT COPIED. <<<
// v1 keeps THREE label sources for five category codes — the card and the
// detail page each hard-code an English map (`categoryLabelMap` = MILLING /
// TURNING / ..., CatalogModelCard.tsx:33-39) while only the tabs translate.
// Data package §0 rule 4 and §14 both name this as a gap to FIX. So: ONE map,
// from code to an i18n key, used by the card, the aside and the dialog.
// Artboards 0900-0903 draw the badge as the OPERATION (FRÉZOVANIE /
// SÚSTRUŽENIE / BRÚSENIE), which is a different string from the category TAB
// label (Obrábacie centrá) — hence its own key set rather than reusing
// `machiningCenters` et al.
//
// The map is `Record<string, string>` and not a closed enum on purpose: §13
// flags the five-code set as "verify live set", and an unknown code must render
// its raw code rather than a blank badge (v1's `|| model.categoryCode`).
//
// COLOURS ARE THE PALETTE'S, NOT v1's. v1 tints each category with a fixed hex
// from `dt` (blue / purple / orange / green / red). Those are dt tokens, which
// §3 forbids in v2, and a fixed hex cannot follow two palettes. The badge uses
// the module accent like every other v2 chip; the CATEGORY is carried by the
// word, which is now translated and therefore actually readable.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { getReferenceLabel } from "../../../../shared/utils/machineReferenceLabels";
import { CatalogModelListItem } from "../../models/models";

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  "machining-centers": "myMachinery:v2.catalog.catMilling",
  "turning-machines": "myMachinery:v2.catalog.catTurning",
  "grinding-machines": "myMachinery:v2.catalog.catGrinding",
  edm: "myMachinery:v2.catalog.catEdm",
  "laser-machines": "myMachinery:v2.catalog.catLaser",
  "packaging-lines": "myMachinery:v2.catalog.catPackaging",
};

export const useCategoryLabel = (): ((code?: string) => string) => {
  const { translate } = useTranslation("myMachinery");
  return (code?: string) => {
    if (!code) return "";
    const key = CATEGORY_LABEL_KEYS[code];
    return key ? translate(key) : code;
  };
};

export const CategoryBadge: React.FC<{ code?: string }> = ({ code }) => {
  const label = useCategoryLabel()(code);
  if (!label) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        padding: "0 8px",
        borderRadius: 6,
        background: "var(--mod-accent-soft)",
        border: "1px solid var(--mod-accent-line)",
        color: "var(--mod-accent)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
};

/** Neutral chip — subtype, taper, key specs. `mono` for values, sans for prose. */
export const CatalogChip: React.FC<{
  children: React.ReactNode;
  mono?: boolean;
  title?: string;
}> = ({ children, mono = true, title }) => (
  <span
    title={title}
    style={{
      display: "inline-flex",
      alignItems: "center",
      height: 21,
      padding: "0 8px",
      borderRadius: 6,
      background: "var(--card-2)",
      border: "1px solid var(--border)",
      color: "var(--muted)",
      fontFamily: mono ? "var(--font-mono)" : "inherit",
      fontSize: mono ? 10 : 11.5,
      whiteSpace: "nowrap",
      minWidth: 0,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flexShrink: 0,
    }}
  >
    {children}
  </span>
);

/**
 * §11: the key-specs row is gated on (rpm || hp || tools || axes || taper).
 *
 * GATE COMPOSITION — RESOLVED IN FULL. This block recorded it first as an OPEN
 * artboard-vs-data-package conflict, then as a PARTIAL resolution that admitted
 * `numAxes` and left `taperTypeName` out; both fields are now settled, so the
 * partial record is gone with them. The data package gates on rpm/hp/tools
 * only. ONE TEST decides each of the other two: the chip already EXISTS below,
 * is already correctly GUARDED, and only the GATE excluded it — so a model
 * whose sole key spec was that field rendered no chip row AT ALL, which is a
 * bug and not a design.
 *   · `numAxes` passes that test. Artboard 0900 also agrees positively: K'MX
 *     620 carries an axis chip ("6+2 osí") and NO tools chip, and on a LOOK
 *     question the artboard wins.
 *   · `taperTypeName` passes it identically (chip at :210). No artboard draws a
 *     taper-only model — but none forbids one either, and an absence of drawn
 *     cases is not a prohibition. Lathes and grinders classified by taper alone
 *     are plausible in real catalog data. v1's rule for it ("purple chip in
 *     card key-specs, does NOT gate") is superseded here.
 *
 * THE INVARIANT: the gate and the chips can NEVER disagree. Each gate test is
 * the SAME test as its own chip's guard — `!= null` for the four numeric ones,
 * TRUTHINESS for `taperTypeName`, which is a `string` (models.ts:238
 * `taperTypeName?: string`) whose chip guard is truthiness. A `!= null` test on
 * it would admit `""` to the gate while the chip still rendered nothing —
 * reintroducing the exact empty-row bug this gate exists to prevent. Change a
 * chip's guard and you change its gate test in the same edit.
 *
 * NULLISH TEST — `!= null`, NEVER `!== undefined`. The three spec columns are
 * nullable server-side (Areas/Customer/Models/Catalog/CatalogModelListItem-
 * ViewModel.cs:34/35/37 — `int?` / `decimal?` / `int?`) and
 * GoToStock.Machinery.WebApi/Program.cs:35 registers a bare
 * `AddControllers()` with no JsonOptions, so System.Text.Json's default EMITS
 * `"maxSpindleRpm": null` rather than omitting the key. `null !== undefined`
 * is `true`, so the old test passed a null into `num(...)` and
 * `null.toLocaleString()` threw mid-render; with nothing catching it React 18
 * unmounted the whole root — the blank /machinery-v2/catalog the owner hit.
 * The same slip made this gate return `true` for EVERY model, force-rendering
 * a chip row the artboard draws as absent. `!= null` catches null AND
 * undefined while preserving a legitimate `0`, and is the idiom already used
 * at fleet/FleetCard.tsx:223. `numAxes` was the one field typed honestly
 * (models.ts:228 `number | null`) and the one field guarded correctly; the
 * other three are now aligned with the server too.
 *
 * DO NOT ASSUME THE COMPILER GUARDS THIS. It does not. `!== undefined` narrows
 * `number | null | undefined` to `number | null`, which `num` — deliberately
 * nullish-TOLERANT below — still accepts, so a relapse type-checks clean.
 * MEASURED, not assumed: one guard was reverted to `!== undefined` and
 * `tsc --noEmit` passed with no error. What the two changes together DO buy is
 * that a relapse can no longer white-screen the route; it degrades to a
 * cosmetic orphan " RPM" chip. The `!= null` test is the only thing keeping
 * that chip off the card, so keep it.
 *
 * Every unit suffix here was HARD-CODED ENGLISH in v1 ("{n} RPM" / "{n} HP" /
 * "{n} tools" / "{n}-axis") and is listed in §14's gap list — all four are now
 * real keys in both locales.
 */
export const catalogHasKeySpecs = (model: CatalogModelListItem): boolean =>
  model.maxSpindleRpm != null ||
  model.spindleHp != null ||
  model.toolCapacity != null ||
  model.numAxes != null ||
  !!model.taperTypeName;

export const CatalogKeySpecs: React.FC<{ model: CatalogModelListItem }> = ({ model }) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  if (!catalogHasKeySpecs(model)) return null;

  // Nullish-TOLERANT by contract. This was `(value: number): string`, which is
  // exactly what let a runtime null past a `!== undefined` guard and throw.
  // Returning `null` for a nullish input means an unguarded call can no longer
  // crash a render, and any call site needing a real string must handle the
  // null. Defence in depth BEHIND the `!= null` guards below, NOT a substitute
  // for them: on its own it would emit an orphan " RPM" chip, and the artboard
  // convention for an absent value is to OMIT the chip, never to placeholder it.
  const num = (value: number | null | undefined): string | null =>
    value == null ? null : value.toLocaleString(getCurrentLanguage());

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, minWidth: 0 }}>
      {model.maxSpindleRpm != null && (
        <CatalogChip>
          {translate("myMachinery:v2.catalog.specRpm", { value: num(model.maxSpindleRpm) })}
        </CatalogChip>
      )}
      {model.spindleHp != null && (
        <CatalogChip>
          {translate("myMachinery:v2.catalog.specHp", { value: num(model.spindleHp) })}
        </CatalogChip>
      )}
      {model.toolCapacity != null && (
        <CatalogChip>
          {translate("myMachinery:v2.catalog.specTools", { value: num(model.toolCapacity) })}
        </CatalogChip>
      )}
      {model.numAxes != null && (
        <CatalogChip>
          {translate("myMachinery:v2.catalog.specAxes", { value: num(model.numAxes) })}
        </CatalogChip>
      )}
      {model.taperTypeName && (
        <CatalogChip>
          {getReferenceLabel("taper", { name: model.taperTypeName, code: model.taperTypeCode }, translate)}
        </CatalogChip>
      )}
    </div>
  );
};

/**
 * §11 image rows: `imageStandardUrl` is the PREFERRED thumb, `imageUrl` the
 * last resort, and neither being present is a normal state for a large part of
 * the catalog ("BEZ FOTOGRAFIE" on artboard 0900). The placeholder is a real
 * labelled panel rather than a broken img, so a missing photo never reads as a
 * load failure.
 */
export const catalogThumbUrl = (model: CatalogModelListItem): string | null =>
  model.imageStandardUrl || model.imageUrl || null;

export const CatalogThumb: React.FC<{
  url: string | null;
  alt: string;
  width?: number | string;
  height?: number;
  radius?: number;
}> = ({ url, alt, width = 88, height = 62, radius = 8 }) => {
  const { translate } = useTranslation("myMachinery");

  const shell: React.CSSProperties = {
    width,
    height,
    borderRadius: radius,
    background: "var(--card-2)",
    border: "1px solid var(--border-soft)",
    flexShrink: 0,
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
  };

  if (!url) {
    return (
      <div style={shell}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 8,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--faint)",
            textAlign: "center",
            padding: "0 4px",
            lineHeight: 1.3,
          }}
        >
          {translate("myMachinery:v2.catalog.noPhoto")}
        </span>
      </div>
    );
  }

  return (
    <div style={shell}>
      <img
        src={url}
        alt={alt}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
      />
    </div>
  );
};
